// Static catalog data for Zenith Ranking
import {
  Megaphone,
  Globe,
  PenLine,
  Link as LinkIcon,
  MapPin,
  ListChecks,
  Search,
  CalendarRange,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "press-release-distribution",
    title: "Press Release Distribution",
    icon: Megaphone,
    short: "Distribute PR content to news and trusted media networks.",
    starting_price: 125,
    benefits: [
      "Wide media reach across 200+ outlets",
      "Brand authority & topical mentions",
      "Boosts referral traffic and visibility",
    ],
    included: [
      "Editor-reviewed press release write-up",
      "Distribution to news network + Google News",
      "Live pickup report + analytics",
    ],
  },
  {
    slug: "content-syndication",
    title: "Content Syndication",
    icon: Globe,
    short: "Republish content across trusted websites for visibility and SEO signals.",
    starting_price: 75,
    benefits: ["Broader audience reach", "Co-citation SEO signals", "Stronger topical authority"],
    included: ["Article adaptation per network", "100+ syndication partners", "Branded canonical links"],
  },
  {
    slug: "guest-posting",
    title: "Guest Posting",
    icon: PenLine,
    short: "Manual outreach for niche-relevant guest post placements by DR tier.",
    starting_price: 89,
    benefits: ["Authority backlinks", "Niche-relevant placements", "SEO-safe and white-label ready"],
    included: ["Manual outreach", "Custom-written content", "Real, indexed websites"],
  },
  {
    slug: "high-end-backlinks",
    title: "High-End Backlinks",
    icon: LinkIcon,
    short: "Premium editorial backlinks from publications with real organic traffic.",
    starting_price: 449,
    benefits: ["Editorial trust", "Strong ranking lift", "Brand-safe placements"],
    included: ["DR 40+ editorial sites", "Organic traffic verified", "Reporting + screenshots"],
  },
  {
    slug: "local-citations",
    title: "Local Citations",
    icon: MapPin,
    short: "Business listings & citation building for local SEO across major directories.",
    starting_price: 125,
    benefits: ["NAP consistency", "Local rank improvements", "Google Business support"],
    included: ["Curated directories", "Country-specific submissions", "Login & report delivery"],
    countries: ["USA", "UK", "Australia", "Canada"],
  },
  {
    slug: "on-page-seo",
    title: "On-Page SEO",
    icon: ListChecks,
    short: "Page optimization, metadata, structure, internal linking, and keyword targeting.",
    starting_price: 99,
    benefits: ["Higher click-through", "Better keyword targeting", "Improved crawlability"],
    included: ["Title & meta rewrites", "Header & schema fixes", "Internal linking blueprint"],
  },
  {
    slug: "site-audit",
    title: "Site Audit",
    icon: Search,
    short: "Detailed SEO audit covering technical health, content gaps, indexing & speed.",
    starting_price: 149,
    benefits: ["Find blockers fast", "Prioritized fixes", "Roadmap for growth"],
    included: ["Technical SEO scan", "Content gap analysis", "Action plan + recording"],
  },
  {
    slug: "blog-content-plan",
    title: "Content Strategy",
    icon: CalendarRange,
    short: "One-time SEO content strategy: keyword clusters, calendars, intent mapping & topics.",
    starting_price: 149,
    benefits: ["Strategy clarity", "Topical clusters", "Search-intent alignment"],
    included: ["Keyword research", "30-day content plan", "Brief templates"],
  },
];

export const PACKAGES = {
  "press-release-distribution": [
    {
      id: "pr-basic",
      name: "Basic",
      price: 125,
      turnaround: "5-7 days",
      features: [
        "200+ premium media outlets",
        "Includes StreetInsider, NewsBreak, TimeBusinessNews, AP News",
        "Pickup report + live links",
      ],
    },
    {
      id: "pr-standard",
      name: "Standard",
      price: 299,
      turnaround: "3-5 days",
      features: [
        "350+ premium media outlets",
        "Featured on StreetInsider, NewsBreak, TimeBusinessNews, AP News",
        "Editorial polish + Google News pickup",
        "Enhanced analytics report",
      ],
      highlight: true,
    },
  ],
  "content-syndication": [
    { id: "cs-basic", name: "Basic", price: 75, turnaround: "7 days", features: ["100+ syndication links", "Starter authority signals", "Live-links report"] },
    { id: "cs-standard", name: "Standard", price: 149, turnaround: "5 days", features: ["250+ syndication links", "Authority partner network", "Detailed placements report"], highlight: true },
  ],
  "guest-posting": [
    { id: "gp-dr10", name: "DR 10-30", price: 89, turnaround: "10-14 days", dr: "DR 10-30", features: ["Budget-friendly placements", "Niche relevance", "Custom content included"] },
    { id: "gp-dr30", name: "DR 30-40", price: 129, turnaround: "10-14 days", dr: "DR 30-40", features: ["Mid-tier authority", "Niche relevance", "Content included"] },
    { id: "gp-dr40", name: "DR 40-50", price: 189, turnaround: "12-16 days", dr: "DR 40-50", features: ["Strong SEO placements", "Manual outreach", "Content included"], highlight: true },
    { id: "gp-dr50", name: "DR 50-60", price: 279, turnaround: "12-18 days", dr: "DR 50-60", features: ["Premium authority", "Editorial review", "Content included"] },
    { id: "gp-dr60", name: "DR 60-70", price: 419, turnaround: "14-21 days", dr: "DR 60-70", features: ["High-authority sites", "Real organic traffic", "Content included"] },
    { id: "gp-dr70", name: "DR 70+", price: 699, turnaround: "21-28 days", dr: "DR 70+", features: ["Elite editorial placements", "Brand-safe", "Content included"] },
  ],
  "high-end-backlinks": [
    { id: "hb-core", name: "Core", price: 449, turnaround: "14 days", features: ["3 editorial links included", "DR 40+ editorial sites", "Traffic verified", "Placement report"] },
    { id: "hb-premium", name: "Premium", price: 899, turnaround: "21 days", features: ["3 editorial links included", "DR 70+ flagship publications", "Niche fit guarantee", "Screenshots + full report"], highlight: true },
  ],
  "local-citations": [
    { id: "lc-starter", name: "Starter", price: 125, turnaround: "7 days", features: ["50 directory listings", "1 country", "NAP consistency"] },
    { id: "lc-pro", name: "Pro", price: 225, turnaround: "10 days", features: ["100 directory listings", "Premium curated directories", "Logins + report"], highlight: true },
    { id: "lc-global", name: "Global Starter", price: 499, turnaround: "14 days", features: ["300 directory listings", "Multi-country coverage", "GBP optimization"] },
  ],
  "on-page-seo": [
    { id: "op-basic", name: "Basic", price: 99, turnaround: "5 days", features: ["Report only", "Full on-page audit", "Meta, headers & internal linking recommendations"] },
    { id: "op-pro", name: "Standard", price: 299, turnaround: "7 days", features: ["Full implementation", "Meta + headers deployed", "Content rewrites shipped", "Schema + speed hints applied"], highlight: true },
  ],
  "site-audit": [
    { id: "audit-core", name: "Core", price: 149, turnaround: "5 days", features: ["Technical SEO scan", "Top 25 issues", "Action plan"] },
    { id: "audit-pro", name: "Pro", price: 349, turnaround: "7 days", features: ["Full crawl", "Content gap + competitors", "Loom walkthrough"], highlight: true },
  ],
  "blog-content-plan": [
    { id: "bp-monthly", name: "Basic", price: 149, turnaround: "5 days", features: ["30-day content plan", "Keyword clusters", "Intent mapping"] },
    { id: "bp-quarterly", name: "Standard", price: 499, turnaround: "10 days", features: ["We create & publish articles on your site", "Requires CMS access", "Editorial calendar included"], highlight: true },
  ],
};

export const TESTIMONIALS = [
  {
    name: "Lena Marsh",
    role: "Head of SEO, Aperture Digital",
    quote: "Zenith Ranking feels like an extension of our team. Turnaround is fast and placements consistently land on real, trafficked sites.",
  },
  {
    name: "Daniel Okafor",
    role: "Founder, Northbeam Studio",
    quote: "We white-label everything. Reports look incredible and our agency margins finally make sense.",
  },
  {
    name: "Priya Raman",
    role: "Marketing Lead, Saga SaaS",
    quote: "Three months in and our DR jumped from 28 to 47. Their guest posting tier breakdown is the cleanest I've used.",
  },
];

export const STATS = [
  { value: "1,000+", label: "Campaigns delivered" },
  { value: "150+", label: "Agency clients" },
  { value: "50+", label: "Countries served" },
  { value: "99.4%", label: "On-time delivery" },
];

export const FAQS_GENERIC = [
  { q: "Are the links permanent?", a: "Yes. All links are placed on real, indexed websites and are intended to be permanent. We notify you if any rare removal occurs." },
  { q: "Do you provide the content?", a: "Yes — content is written by our editorial team, reviewed for niche relevance and tone, and approved before placement." },
  { q: "Is everything white-label?", a: "Absolutely. No Zenith Ranking branding appears on any deliverable. Resell with confidence." },
  { q: "What industries do you support?", a: "We work across 80+ niches including SaaS, finance, health, e-commerce, B2B, travel, and local services." },
  { q: "How long does delivery take?", a: "Most services deliver in 5-21 days depending on tier. Live ETAs appear inside your dashboard." },
];

export const BLOG = [
  {
    slug: "guest-posting-without-getting-burned",
    title: "Guest Posting Without Getting Burned: A 2026 Playbook",
    category: "Link Building",
    excerpt: "How to vet sites, brief writers, and avoid the link networks that quietly tank rankings.",
    image: "https://images.unsplash.com/photo-1707336862166-1e483cfa5c92?crop=entropy&cs=srgb&fm=jpg&q=85",
    author: "Editorial Team",
    date: "Mar 14, 2026",
    read: "9 min read",
    toc: ["The DR trap", "Signals of a real publisher", "Brief like a journalist", "Anchor text ratios", "Tracking outcomes"],
    body: [
      { h: "The DR trap", p: "DR is a directional metric. We see countless sites with DR 70+ and zero topical authority — driven by spammy backlinks. Treat DR as one of seven signals, not the goal." },
      { h: "Signals of a real publisher", p: "Look for: editorial calendar, named authors, recent organic traffic on the target topic, niche-relevant outbound links, and a consistent posting cadence." },
      { h: "Brief like a journalist", p: "Lead with a concrete claim, supply two quotes you can attribute, and add original data. The piece earns the placement; the link follows." },
      { h: "Anchor text ratios", p: "Aim for 60% branded, 25% generic/URL, 10% partial-match, 5% exact. Variation across campaigns matters more than perfection on a single piece." },
      { h: "Tracking outcomes", p: "Tag every placement, monitor referring domains for 90 days, and pause partners whose links go thin or noindex." },
    ],
  },
  {
    slug: "press-release-distribution-still-works",
    title: "Press Release Distribution Still Works — If You Use It Like This",
    category: "Digital PR",
    excerpt: "PR has changed. Here's the modern recipe for newsroom-quality distribution that actually drives traffic.",
    image: "https://images.unsplash.com/photo-1755502046551-102bcdc6f0c6?crop=entropy&cs=srgb&fm=jpg&q=85",
    author: "Marcus Hale",
    date: "Feb 27, 2026",
    read: "7 min read",
    toc: ["What PR isn't", "Newsroom recipe", "The 3 angles", "Reporting"],
    body: [
      { h: "What PR isn't", p: "PR isn't a single press release blasted across 400 mirror sites. That model died. The new model is a tight angle, a credible quote, and targeted distribution." },
      { h: "Newsroom recipe", p: "Headline + dateline + lead + 2 quotes + boilerplate + asset URLs. Keep under 600 words. Editors prefer cuttable copy over poetic copy." },
      { h: "The 3 angles", p: "Trend, Data, or Milestone. Pick one — never all three. Trend earns SEO, Data earns links, Milestone earns coverage." },
      { h: "Reporting", p: "Track pickups, organic referrals, and brand mentions for 30 days. Most measurable lift arrives in week 2." },
    ],
  },
  {
    slug: "local-seo-audit-checklist-2026",
    title: "The Local SEO Audit Checklist We Run for Every New Client",
    category: "Local SEO",
    excerpt: "From GBP categories to NAP consistency — the 22 checks we do before recommending any link work.",
    image: "https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    author: "Editorial Team",
    date: "Feb 11, 2026",
    read: "11 min read",
    toc: ["Foundational fixes", "On-page", "Citations", "Reviews", "Links"],
    body: [
      { h: "Foundational fixes", p: "Lock down your GBP primary category, service area accuracy, and verify ownership across all locations before anything else." },
      { h: "On-page", p: "Each location page should have unique copy, embedded map, schema (LocalBusiness), and internal links from city-cluster content." },
      { h: "Citations", p: "Run a NAP scan across the top 50 local directories. Inconsistencies erode rank faster than thin content." },
      { h: "Reviews", p: "Velocity matters more than volume. 2 reviews/week beats 60 in one weekend." },
      { h: "Links", p: "Local sponsorships, chamber listings, and niche directories outperform generic guest posts for map pack lift." },
    ],
  },
  {
    slug: "content-clusters-that-actually-rank",
    title: "Content Clusters That Actually Rank (Not Just Look Pretty)",
    category: "Content Marketing",
    excerpt: "Why most pillar-cluster diagrams fail, and the 4-part structure we use instead.",
    image: "https://images.unsplash.com/photo-1758691736979-ff263c04b3d1?crop=entropy&cs=srgb&fm=jpg&q=85",
    author: "Priya Raman",
    date: "Jan 30, 2026",
    read: "8 min read",
    toc: ["The pillar myth", "4-part structure", "Internal links", "Refresh cycles"],
    body: [
      { h: "The pillar myth", p: "A pillar isn't one giant page — it's a topical center of gravity. Search intent should dictate format." },
      { h: "4-part structure", p: "Definition page · Comparison page · How-to page · Tools/Resource page. Each maps to a different intent and earns its own backlinks." },
      { h: "Internal links", p: "Cluster pages must link both up to the pillar and laterally to each other. Anchor text rotates between exact, partial, and branded." },
      { h: "Refresh cycles", p: "Re-audit clusters every 90 days. Promote freshest article, deprecate stale ones, redirect cannibalizers." },
    ],
  },
  {
    slug: "technical-seo-things-that-still-matter",
    title: "Technical SEO Things That Still Matter in 2026",
    category: "Technical SEO",
    excerpt: "Core Web Vitals, indexability, edge rendering — what to keep, what to drop.",
    image: "https://images.unsplash.com/photo-1743046813915-94cf6d5e6942?crop=entropy&cs=srgb&fm=jpg&q=85",
    author: "Daniel Okafor",
    date: "Jan 12, 2026",
    read: "10 min read",
    toc: ["Vitals worth caring about", "Indexability", "Edge rendering", "Schema sanity"],
    body: [
      { h: "Vitals worth caring about", p: "INP replaced FID. LCP and CLS still matter. TBT is a useful proxy for INP issues." },
      { h: "Indexability", p: "Audit for orphan pages, redirect chains, soft 404s, and parameter explosion before chasing rankings." },
      { h: "Edge rendering", p: "Static-first with selective ISR remains the most reliable pattern. SSR everywhere is overkill." },
      { h: "Schema sanity", p: "Article, Product, FAQ, Breadcrumbs — that's 95% of needs. Don't stuff schema you can't substantiate." },
    ],
  },
  {
    slug: "white-label-seo-for-agencies",
    title: "White-Label SEO for Agencies: A Margin-First Operating Model",
    category: "SEO",
    excerpt: "How to package, price, and scale white-label SEO without the typical fulfillment chaos.",
    image: "https://images.unsplash.com/photo-1758518731296-20e24e58846f?crop=entropy&cs=srgb&fm=jpg&q=85",
    author: "Lena Marsh",
    date: "Dec 18, 2025",
    read: "12 min read",
    toc: ["Margins first", "Productize relentlessly", "SLAs over scope", "Reporting cadence"],
    body: [
      { h: "Margins first", p: "Pick services with > 60% gross margin and fast turnaround. Resist custom scopes until you've proven the productized one." },
      { h: "Productize relentlessly", p: "Three packages, three deliverables, three turnaround windows. Predictability sells." },
      { h: "SLAs over scope", p: "Commit to delivery dates and revision counts. Clients buy certainty, not creativity." },
      { h: "Reporting cadence", p: "Monthly summary deck + dashboard access. Less is more." },
    ],
  },
];

export const CASE_STUDIES = [
  {
    slug: "saas-traffic-growth-340",
    title: "B2B SaaS scales organic traffic by 340% in 6 months",
    client: "Saga SaaS",
    industry: "B2B SaaS",
    image: "https://images.unsplash.com/photo-1758691736979-ff263c04b3d1?crop=entropy&cs=srgb&fm=jpg&q=85",
    challenge: "Plateaued at 12k monthly visits with no growth in DR for 9 months despite weekly content production.",
    strategy: "Restructured content into 4 pillar clusters; added monthly DR 40-60 guest posting; ran 2 PR campaigns around product launches.",
    services: ["Guest Posting", "Press Release Distribution", "On-Page SEO"],
    metrics: [
      { label: "Organic traffic", before: "12k", after: "53k", delta: "+340%" },
      { label: "Domain Rating", before: "28", after: "47", delta: "+19" },
      { label: "Top-10 keywords", before: "84", after: "612", delta: "+628" },
    ],
    quote: "Our pipeline coverage from organic doubled. The clarity of monthly reporting was a game-changer.",
    author: "Priya Raman, Marketing Lead",
  },
  {
    slug: "local-services-map-pack",
    title: "Multi-location HVAC firm wins map pack across 14 cities",
    client: "Northern Flow HVAC",
    industry: "Local Services",
    image: "https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    challenge: "Inconsistent NAP, thin location pages, and zero map pack visibility outside flagship city.",
    strategy: "Local citations rebuild + GBP optimization across 14 locations + local content cluster + targeted DR 30-40 guest posts.",
    services: ["Local Citations", "On-Page SEO", "Guest Posting"],
    metrics: [
      { label: "Map pack visibility", before: "9%", after: "78%", delta: "+69pt" },
      { label: "GBP calls / mo", before: "112", after: "844", delta: "+653%" },
      { label: "Local rankings (top-3)", before: "11", after: "92", delta: "+81" },
    ],
    quote: "Calls into our office tripled within 90 days. Zenith Ranking's local team is in another league.",
    author: "Marcus Hale, Owner",
  },
  {
    slug: "ecommerce-pr-campaign-launch",
    title: "DTC e-commerce launches with 240+ press pickups in 30 days",
    client: "Aperture Goods",
    industry: "E-commerce",
    image: "https://images.unsplash.com/photo-1755502046551-102bcdc6f0c6?crop=entropy&cs=srgb&fm=jpg&q=85",
    challenge: "Pre-launch DTC brand needed authority signal and referral traffic without paid media spend.",
    strategy: "3-wave PR distribution + content syndication on Tier 1 lifestyle outlets + DR 60+ flagship backlinks.",
    services: ["Press Release Distribution", "Content Syndication", "High-End Backlinks"],
    metrics: [
      { label: "Press pickups (30d)", before: "0", after: "243", delta: "+243" },
      { label: "Referral sessions", before: "—", after: "31k", delta: "+31k" },
      { label: "Branded search volume", before: "—", after: "8.4k/mo", delta: "+8.4k" },
    ],
    quote: "The launch felt orchestrated by a top-tier PR firm. Costs were a fraction of agency quotes we received.",
    author: "Lena Marsh, Head of Growth",
  },
];
